import * as THREE from 'three';
import { keyCodes } from './KeyboardHandler';

import $ from 'jquery';

export const INPUT_EDGE_MARGIN = 8;

export class InputHandler {
  constructor(imageEngine) {
    this.imageEngine = imageEngine;
    this._inputs = [];
    this._savedInputs = [];
    this._onInputScroll = undefined;
    this._onKeyboardInput = undefined;
  }

  _createInputContainer(input) {
    const isTouch = this.imageEngine.mouseHandler.isUsingTouch;
    const div = $(
      `<div class="wd-input-container"><div class="wd-input-container-header">Header</div><div class="wd-inp-position-content"><input type="${
        isTouch ? 'number' : 'text'
      }" class="wd-inp-pos" /><span type="text" class="wd-mullion-length">?</span></div></div>`
    );
    div.find('.wd-inp-pos').on('keydown', e => {
      if (e.which !== keyCodes.escape) {
        e.stopPropagation();
      }

      if (e.which === keyCodes.enter) {
        const entry = this._inputs.find(x => x.container === div);
        if (entry) {
          this.submitInput(entry);
        }
      }
    });

    let isDragging = false;
    let dragCurrX = undefined;
    let dragCurrY = undefined;
    div.find('.wd-input-container-header').on('mousedown touchstart', e => {
      isDragging = true;
      dragCurrX = parseFloat(input.container.css('left'));
      dragCurrY = parseFloat(input.container.css('top'));
      e.preventDefault();
    });
    const onMouseMove = (dx, dy) => {
      if (!isDragging) return;

      input.autoPosition = false;

      dragCurrX += dx;
      dragCurrY += dy;

      this._updateInputPosition(input, dragCurrX, dragCurrY);
    };
    const onMouseUp = () => {
      isDragging = false;
    };
    const onResize = () => {
      if (
        !input.autoPosition &&
        dragCurrX !== undefined &&
        dragCurrY !== undefined
      ) {
        this._updateInputPosition(input, dragCurrX, dragCurrY);
      }
    };
    this.imageEngine.mouseHandler.addMouseMoveHandler(onMouseMove);
    this.imageEngine.mouseHandler.addMouseUpHandler(onMouseUp);
    window.addEventListener('resize', onResize);
    input.dispose = () => {
      this.imageEngine.mouseHandler.removeMouseMoveHandler(onMouseMove);
      this.imageEngine.mouseHandler.removeMouseUpHandler(onMouseUp);
      window.removeEventListener('resize', onResize);
    };
    return div;
  }

  _createInputButtonContainer() {
    const div = $(
      '<div class="wd-input-options"><div class="wd-input-options-left"></div><div class="wd-input-options-right"></div></div>'
    );
    return div;
  }

  _createInputButton(options, entry) {
    const button = $('<button class="wd-button"></button>');

    if (options.text !== undefined) {
      button.text(options.text);
    }
    if (options.onClick) {
      button.on('click', () => {
        const clickOptions = {
          submit: () => this.submitInput(entry),
        };
        options.onClick(clickOptions);
      });
    }

    return button;
  }

  isInputOpen() {
    return this._inputs.length > 0;
  }

  _createInputEntry(options) {
    const entry = this._savedInputs.splice(0, 1)[0] || {};
    const value =
      options.defaultValue === undefined
        ? ''
        : Number.isNaN(options.defaultValue)
        ? options.defaultValue.toString()
        : options.defaultValue.toString().replace('.', ',');
    const inputContainer = entry.container || this._createInputContainer(entry);

    inputContainer.css('display', '');
    inputContainer.find('.wd-input-container-header').text(options.headerText);
    inputContainer.find('.wd-mullion-length').text(options.appendText || '');
    inputContainer.find('.wd-inp-pos').val(value);
    if (entry.autoPosition) {
      const coords = options.getCoords(inputContainer);
      inputContainer.css({
        top: coords.y,
        left: coords.x,
      });
    }

    if (options.buttons) {
      const buttonContainer = this._createInputButtonContainer();
      const leftButtonContainer = buttonContainer.find(
        '.wd-input-options-left'
      );
      const rightButtonContainer = buttonContainer.find(
        '.wd-input-options-right'
      );
      for (const buttonOptions of options.buttons) {
        const button = this._createInputButton(buttonOptions, entry);
        if (buttonOptions.side === 'right') {
          rightButtonContainer.append(button);
        } else {
          leftButtonContainer.append(button);
        }
      }
      inputContainer.find('.wd-input-options').remove();
      inputContainer.append(buttonContainer);
    }

    entry.container = entry.container || inputContainer;
    entry.getCoords = options.getCoords;
    entry.onSubmit = options.onSubmit;
    if (entry.autoPosition === undefined) {
      entry.autoPosition = true;
    }
    return entry;
  }

  requestInputs(options) {
    if (this._inputs.length > 0) {
      return;
    }

    for (const inputOptions of options.inputs) {
      const entry = this._createInputEntry(inputOptions);
      this._inputs.push(entry);
      $(this.imageEngine.container).append(entry.container);
    }

    this._onInputScroll = options.onScroll;
    this._onKeyboardInput = options.onInput;
  }

  submitInput(entry) {
    if (entry.onSubmit) {
      const value = entry.container.find('.wd-inp-pos').val();
      entry.onSubmit(value);
    }
  }

  _updateInputPosition(entry, x, y) {
    const rendererSize = this.imageEngine.renderer.getSize(new THREE.Vector2());
    const minX = INPUT_EDGE_MARGIN;
    const maxX =
      rendererSize.width - entry.container.width() - INPUT_EDGE_MARGIN;
    const minY = INPUT_EDGE_MARGIN;
    const maxY =
      rendererSize.height - entry.container.height() - INPUT_EDGE_MARGIN;

    x = Math.max(minX, Math.min(maxX, x));
    y = Math.max(minY, Math.min(maxY, y));

    entry.container.css({
      left: x,
      top: y,
    });
  }

  updatePosition() {
    for (const entry of this._inputs) {
      if (!entry.autoPosition) continue;

      const coords = entry.getCoords(entry.container);
      this._updateInputPosition(entry, coords.x, coords.y);
    }
  }

  onKeyDown(e) {
    if (!this.isInputOpen()) {
      return;
    }

    if (this._onKeyboardInput) {
      this._onKeyboardInput(e.which);
    }
  }

  onMouseScroll(e) {
    if (!this.isInputOpen()) {
      return;
    }

    if (this._onInputScroll) {
      this._onInputScroll(e.deltaY);
    }
  }

  closeInputs() {
    for (const entry of this._inputs) {
      entry.dispose();
      entry.container.remove();
    }
    this._inputs = [];
    this._onInputScroll = undefined;
    this._onKeyboardInput = undefined;
  }

  saveAndCloseInputs() {
    for (const entry of this._inputs) {
      entry.container.css('display', 'none');
    }
    this._savedInputs = this._inputs;
    this._inputs = [];
  }
}
